import type { TFunction } from 'i18next';

import type { DailyForecastUiRow } from '@/features/dashboard/utils/openMeteo/dailyToUiRows';
import {
  describeWmoWeatherCode,
  wmoCodeToUiIcon,
} from '@/features/dashboard/utils/openMeteo/wmoWeather';

function weatherIconUrl2x(icon: string): string {
  return `https://openweathermap.org/img/wn/${icon}@2x.png`;
}

export function DailyForecastCard({ row, t }: { row: DailyForecastUiRow; t: TFunction }) {
  const icon = wmoCodeToUiIcon(row.weatherCode);
  const desc = describeWmoWeatherCode(row.weatherCode, t);
  const range =
    row.tempMin != null && row.tempMax != null
      ? t('weather.dailyTempRange', {
          min: Math.round(row.tempMin * 10) / 10,
          max: Math.round(row.tempMax * 10) / 10,
        })
      : row.tempMax != null
        ? `${Math.round(row.tempMax * 10) / 10}°`
        : row.tempMin != null
          ? `${Math.round(row.tempMin * 10) / 10}°`
          : '—';
  const precipLine =
    row.precipitationMm != null && row.precipitationMm > 0
      ? t('weather.dailyPrecipSum', { mm: Math.round(row.precipitationMm * 10) / 10 })
      : t('weather.dailyPrecipNone');

  return (
    <div
      style={{
        scrollSnapAlign: 'start',
        flex: '0 0 auto',
        width: 132,
        boxSizing: 'border-box',
        padding: '10px 10px 12px',
        borderRadius: 12,
        border: '1px solid var(--glass-border)',
        background:
          'linear-gradient(165deg, rgba(59, 130, 246, 0.1) 0%, var(--bg-card) 55%, var(--map-chrome-deep) 100%)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--accent-blue)',
          marginBottom: 8,
        }}
      >
        {row.dateLabel}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <img
          src={weatherIconUrl2x(icon)}
          alt=""
          width={56}
          height={56}
          style={{ display: 'block' }}
        />
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'center',
          marginBottom: 6,
          color: 'var(--text-primary)',
        }}
      >
        {range}
      </div>
      <div
        style={{
          fontSize: 11,
          lineHeight: 1.35,
          color: 'var(--text-secondary)',
          textAlign: 'center',
          minHeight: 32,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {desc}
      </div>
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--glass-border)',
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}
      >
        {precipLine}
      </div>
    </div>
  );
}
